import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SnackbarField } from '@/components/common';
import { queryRoleList, Role } from '@/services/manageService/roleService';
import { PopoverButton } from '@/components/common';
import { InfoOutlined } from '@mui/icons-material';
import RoleDetail from '@/pages/components/manage/sys/role/RoleDetail';
import RoleBindDrawer from '@/pages/components/manage/sys/role/RoleBindDrawer';
import {usePageCrud} from '@/hooks/usePageCrud';
import {useAuthMenu} from '@/services/authService/authMenu';
import {hasButtonPermission} from '@/utils/menuUtils';

// 角色类型映射
const ROLE_TYPE_MAP: Record<number, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  [-1]: { label: 'Custom Role', color: 'default' },
  0: { label: 'Super Admin', color: 'primary' },
  1: { label: 'Admin Role', color: 'secondary' },
  2: { label: 'Accounting Role', color: 'info' },
  3: { label: 'Audit Role', color: 'success' },
};

// 数据权限映射
const DATA_ACCESS_MAP: Record<number, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  1: { label: 'owner', color: 'default' },
  2: { label: 'Owner and subordinates', color: 'primary' },
  3: { label: 'dept', color: 'secondary' },
  4: { label: 'dept and dept subordinates', color: 'info' },
  5: { label: 'all', color: 'success' },
};

const RoleList: React.FC = () => {
  const theme = useTheme();
  const [rows, setRows] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [roleDetailOpen, setRoleDetailOpen] = useState(false);
  const [isAdd, setIsAdd] = useState(true);
  const [detailRoleId, setDetailRoleId] = useState<number | undefined>(undefined);
  const [bindOpen, setBindOpen] = useState(false);
  const [bindRoleId, setBindRoleId] = useState<number | undefined>(undefined);
  const [bindRoleType, setBindRoleType] = useState<number | undefined>(undefined);
  const [bindRoleName, setBindRoleName] = useState<string | undefined>(undefined);
  const {canRead, canCreate, canRemove} = usePageCrud({
    read: 'admin_role_query',
    create: 'admin:role:create',
    update: 'admin:role:update',
    remove: 'admin:role:delete',
  });
  const {data: menuTree = []} = useAuthMenu();
  const canBind = hasButtonPermission(menuTree, 'admin:relation:user');

  useEffect(() => {
    setLoading(true);
    queryRoleList()
      .then((res) => {
        setRows(res || []);
        setError(null);
      })
      .catch((e) => {
        const msg = e?.message || 'Load failed';
        setError(msg);
        setSnackbar({ open: true, message: msg, severity: 'error' });
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await queryRoleList();
      setRows(res || []);
      setError(null);
    } catch (e: any) {
      const msg = e?.message || 'Load failed';
      setError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setIsAdd(true);
    setDetailRoleId(undefined);
    setRoleDetailOpen(true);
  };

  const handleDetail = (roleId: number) => {
    setIsAdd(false);
    setDetailRoleId(roleId);
    setRoleDetailOpen(true);
  };

  const handleOpenBind = (role: Role) => {
    setBindRoleId(role.roleId);
    setBindRoleType(role.roleType);
    setBindRoleName(role.roleName);
    setBindOpen(true);
  };

  const handleBindClose = (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => {
    setBindOpen(false);
    setBindRoleId(undefined);
    setBindRoleType(undefined);
    setBindRoleName(undefined);
    if (payload?.message) {
      setSnackbar({ open: true, message: payload.message, severity: payload.severity || 'success' });
    }
    if (payload?.refreshed) fetchData();
  };

  const handleRoleDetailClose = (data?: any) => {
    setRoleDetailOpen(false);
    if (data && data.message) {
      setSnackbar({ open: true, message: data.message, severity: data.severity || 'success' });
    }
    fetchData();
  };

  const renderStatusChip = (v: number) => {
    if (v === 1) return <Chip label="Enabled" color="success" size="small" />;
    if (v === 0) return <Chip label="Disabled" color="error" size="small" />;
    return <Chip label={String(v)} size="small" />;
  };

  const renderHiddenChip = (v: number) => {
    return v === 1 ? <Chip label="Hidden" color="warning" size="small" /> : <Chip label="Visible" color="info" size="small" />;
  };

  const renderRoleType = (v: number) => {
    const mapping = ROLE_TYPE_MAP[v];
    if (!mapping) return <Chip label={String(v)} size="small" />;
    return <Chip label={mapping.label} color={mapping.color} size="small" />;
  };

  const renderDataType = (v: number) => {
    const mapping = DATA_ACCESS_MAP[v];
    if (!mapping) return <Chip label={String(v)} size="small" />;
    return <Chip label={mapping.label} color={mapping.color} size="small" />;
  };

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{
      p: 2,
      bgcolor: 'transparent',
      height: 'calc(100vh - 120px)',
      minHeight: '70vh',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Role List
        </Typography>
        {canCreate && (
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAdd}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: theme.shape.borderRadius,
          }}
        >
          ADD
        </Button>
        )}
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          boxShadow: theme.shadows[2],
          bgcolor: 'background.paper',
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: 650,
            tableLayout: 'fixed',
            '& .MuiTableCell-stickyHeader': {
              bgcolor: 'background.paper',
              zIndex: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Role Name</TableCell>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Role Type</TableCell>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Data Access</TableCell>
              <TableCell sx={{ width: 120, fontWeight: 700 }}>Hidden</TableCell>
              <TableCell sx={{ width: 220, fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.roleId} hover sx={{ cursor: 'pointer', '&:hover': { background: theme.palette.action.selected } }}>
                <TableCell sx={{ width: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.roleName}</TableCell>
                <TableCell sx={{ width: 120 }}>{renderRoleType(r.roleType)}</TableCell>
                <TableCell sx={{ width: 120 }}>{renderStatusChip(r.status)}</TableCell>
                <TableCell sx={{ width: 120 }}>{renderDataType(r.dataType)}</TableCell>
                <TableCell sx={{ width: 120 }}>{renderHiddenChip(r.isHidden)}</TableCell>
                <TableCell sx={{ width: 220 }}>
                  <Stack direction="row" spacing={1}>
                    {canRead && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        minWidth: 80,
                        textTransform: 'none',
                        borderColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.main,
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: theme.palette.secondary.dark,
                          color: theme.palette.secondary.dark,
                        },
                      }}
                      startIcon={<InfoOutlined />}
                      onClick={() => handleDetail(r.roleId)}
                    >
                      Detail
                    </Button>
                    )}
                    {canBind && (
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      sx={{ minWidth: 80, textTransform: 'none' }}
                      onClick={() => handleOpenBind(r)}
                    >
                      Bind
                    </Button>
                    )}
                    {canRemove && String(r.roleName ?? '').toLowerCase() !== 'superadmin' && (
                    <PopoverButton
                      nodeId={r.roleId}
                      nodeName={r.roleName}
                      onRemove={async (roleId: number) => {
                        try {
                          const { deleteRole } = await import('@/services/manageService/roleService');
                          const res: any = await deleteRole(roleId);
                          if (res && (res.code === '200' || res.code === 200 || res.success)) {
                            setSnackbar({ open: true, message: 'Role Delete Successful!', severity: 'success' });
                          } else {
                            const msg = (res && (res.msg || res.message)) || 'Role Delete failed!';
                            setSnackbar({ open: true, message: msg, severity: 'error' });
                          }
                          fetchData();
                        } catch (e: any) {
                          setSnackbar({ open: true, message: e?.message || 'Role Delete failed!', severity: 'error' });
                        }
                      }}
                      theme={theme}
                    />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">NO DATA YET</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <RoleDetail
        open={roleDetailOpen}
        onClose={handleRoleDetailClose}
        isAdd={isAdd}
        roleId={detailRoleId}
      />
      <RoleBindDrawer
        open={bindOpen}
        roleId={bindRoleId}
        roleType={bindRoleType}
        roleName={bindRoleName}
        onClose={handleBindClose}
      />
      <SnackbarField
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default RoleList;
